export type CustomerGroup = {
  customerGroupName: string;
  customerGroupId: string;
};

export type Contract = {
  contractId: string;
  contractType: string;
  contractTypeCode: string;
  assetOwnership: string;
  contractStartDate: string;
};

export type MeterPointDetails = {
  mprn?: string;
  customerGroupId?: string;
  contractId?: string;
  contractSiteStatusId?: string;
  effectiveDate?: string;
  siteReferenceName?: string;
  isImport?: 'Y' | 'N';
  isExport?: 'Y' | 'N';
  siteTypeId?: string;
  assetOwnership?: string | null;
  leasePurchaseTerm?: string;
  relatedMprn?: string;
  address?: Address;
  contact?: SiteContact;
  contactAddress?: Address;
  invoiceGroupId?: string;
  batchName?: string;
  meterWorkRequired?: string; // '1' | '2' | '3' | '4' | '5' | '9';
  multiPersonJob?: 'Y' | 'N';
  parkingAvailable?: 'Y' | 'N';
  parkingNotes?: string;
  meterLocationNotes?: string;
  mobileSignal?: 'Y' | 'N';
  noCommsId?: string | null;
  btInstallDeclined?: 'Y' | 'N';
  custOwnedComms?: 'Y' | 'N';
  previousCampaignCount?: string;
  utilityId?: string;
  animalsOnSite?: 'Y' | 'N';
  siteRef?: string;
  fuseSize?: string;
  // used for transferring and terminating sites only because we can't use generics
  terminationDate?: string;
  transferDate?: string;
  transferCustomerGroupId?: string;
  transferContractId?: string;
  transferContractStartDate?: string;
};

export type SearchMeterPointDetails = MeterPointDetails & {
  contractType: string;
  jobHistory: HistoricJob[];
  appointmentActive: boolean;
};

export const isSearchMeterPointDetails = (obj: any): obj is SearchMeterPointDetails => {
  return obj?.jobHistory !== undefined && obj?.contractType !== undefined;
};

export type HistoricJob = {
  jobId: string;
  jobTypeId: string;
  jobTypeCode: string;
  bookingDatetime: string;
  actualVisitDate: string;
  scheduledDatetime: string;
  jobStatusId: string;
  assignedOperative: string;
  bookingComments: string;
  fieldComments: string;
  techNotes: string;
  visitOutcome: string;
  immpJobId: string;
  siteTypeId: string;
  externalBookingId: string;
  jobDuration: string;
};

export type Address = {
  addressId?: string;
  addressName?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  town?: string;
  county?: string;
  postcode?: string;
};

export type SiteContact = {
  siteContactId?: string;
  siteContactTitle?: string;
  siteContactFirstname?: string;
  siteContactSurname?: string;
  siteContactNumber?: string;
  siteContactExtensionNumber?: string;
  emailAddress?: string;
  preferredContactMethod?: string;
  mobileNumber?: string;
  faxNumber?: string;
  primaryContact?: 'Y' | null;
};

export type Invoice = {
  invoiceGroupId: string;
  customerInvoiceName: string;
  defaultInvoiceGroup: boolean;
  contractId?: string;
};

export type StandingDataItem = {
  title: string;
  value: string;
};

export type StandingData = {
  standingDataSiteTypeList: StandingDataItem[];
  standingDataAssetOwnershipList: StandingDataItem[];
  standingDataNoCommsList: StandingDataItem[];
  standingDataSiteStatusList: StandingDataItem[];
  standingDataMeterWorkRequiredList: StandingDataItem[];
  standingDataJobTypeList: StandingDataItem[];
  standingDataJobStatusList: StandingDataItem[];
  standingDataVisitOutcomeList: StandingDataItem[];
};

export type MpanAssignmentPageForm = {
  mpan: string;
  mode: EMpanAssignmentMode;
};

export type BulkAssignListForm = Partial<MeterPointDetails> & {
  siteList: MeterPointDetails[];
  invalidSiteList?: {
    siteDetails: MeterPointDetails;
    errorDescription: string;
  }[];
};

export type BulkAssignFileForm = {
  fileList: FileList;
  batchName?: string;
  contractId: string;
  customerGroupId: string;
};

export type DetailsSearchResultPageItem = {
  mprn: string;
  contractSiteStatusId: string;
  effectiveDate?: string;
  siteReferenceName?: string;
  batchName?: string;
  postcode?: string;
};

export type DetailsSearchPageForm = {
  customerGroupId: string;
  contractId: string;
  contractSiteStatusId?: string;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  batchName?: string;
  siteReferenceName?: string;
  postcode?: string;
};

export type Appointment = {
  active: boolean;
  existingContracts: number[];
  duplicate: boolean;
  hasOpenExceptions: boolean;
  address?: Address;
  contact?: SiteContact;
  effectiveDate?: string;
};

export type HandoverExceptionOverview = {
  handoverFileName: string;
  numberOfExceptions: number;
  numberOfNoAppointmentExceptions: number;
  uploadDate: string;
  contractId: string;
  customerGroupName: string;
};

export type BaseExceptionDetails = {
  siteDetails: MeterPointDetails;
};

export enum EExceptionType {
  NO_APPOINTMENT_EXCEPTION = 'NO_APPOINTMENT_EXCEPTION',
  VALIDATION_EXCEPTION = 'VALIDATION_EXCEPTION'
}

export type HandoverExceptions<ExceptionType extends BaseExceptionDetails = BaseExceptionDetails> = {
  handoverFileName: string;
  contractId: string;
  customerGroupId: string;
  exceptionType: EExceptionType;
  exceptions: ExceptionType[];
};

export type MeterPointDetailsWrapperForm = {
  contractId: string;
  customerGroupId: string;
  editMode?: EMpanEditMode;
};

export enum EMpanAssignmentMode {
  EDIT = 'EDIT',
  NEW = 'NEW',
  VIEW = 'VIEW'
}

export enum EMpanEditMode {
  EDIT = 'EDIT',
  TRANSFER = 'TRANSFER',
  TERMINATE = 'TERMINATE'
}

export type DuplicateHandoverCheckResponse = {
  duplicate: boolean;
};

// region: Exception Overview Types
export type HandoverExceptionOverviewsPage = {
  exceptions: HandoverExceptionOverview[];
  startKey: string;
};

export type ExceptionOverviewForm = {
  searchTerm?: string;
  mode: EExceptionOverviewMode;
};

export enum EExceptionOverviewMode {
  MPAN = 'MPAN',
  HANDOVER_FILE_NAME = 'HANDOVER_FILE_NAME'
}
// #endregion
